/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { addThrottledEvent, Button, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import classNames from 'classnames';
import getHtmlParserValue from '../../Utilities/get-html-parser-value';
import * as React from 'react';

import { Dropdown } from '../../../src/themes/mattressfirm/custom-components/dropdown/dropdown';
import _getViewPort from '../../Utilities/get-view-port';
import { IMfrmAdjustableBaseDimensionsData } from './mfrm-adjustable-base-dimensions.data';
import { IMfrmAdjustableBaseDimensionsProps } from './mfrm-adjustable-base-dimensions.props.autogenerated';

interface IViewNames {
    Flat: string;
    HeadUp: string;
    FeetUp: string;
    ZeroGravity: string;
}
interface IViewDimensions {
    MinHeight?: string;
    MaxHeight?: string;
}

interface ISizeDimensions {
    Name: string;
    Length: string;
    Width: string;
    Height: string;
    Weight: string;
    HeadUp?: IViewDimensions;
    Flat?: IViewDimensions;
    ZeroGravity?: IViewDimensions;
    FeetUp?: IViewDimensions;
}
interface IAdjustableBaseDimensions {
    [index: string]: ISizeDimensions;
}

interface ISizeButtonsData {
    displayName: string | undefined;
    value: string;
    sizeDimensions: string;
}

interface IViewButtonsData {
    value: string;
    displayName: string;
}

export interface IDisplayValues {
    name: string;
    value: string;
}

export interface ISelectedSize {
    dropdownId: string;
    selectId: string;
    selectedValue: string;
}
export interface IState {
    selectedSize: string;
    selectedView: string;
    isMobile: boolean;
    isTablet: boolean;
}
export interface IMfrmAdjustableBaseDimensionsViewProps extends IMfrmAdjustableBaseDimensionsProps<IMfrmAdjustableBaseDimensionsData> {
    viewButtons: React.ReactNode;
    selectedSizeDimensions: React.ReactNode;
    selectedViewDimensions: React.ReactNode;
    displayImage: React.ReactNode;
    sizeSelection: React.ReactNode;
}

const parseBackendData = (result: AttributeValue[] | undefined, backendAttribute: string) => {
    if (result && result.length) {
        // determine if product has adjustable base dimensions
        const backendDataString =
            result?.find(attr => attr.Name?.trim().toLowerCase() === backendAttribute.trim().toLowerCase())?.TextValue || '';
        const backendData = getHtmlParserValue(backendDataString);

        // determine if product is adjustable base
        const productType = result?.find(attr => attr.Name?.trim().toLowerCase() === 'product type')?.TextValue;
        const isAdjustableBase = productType?.trim().toLowerCase() === 'adjustable bases';
        if (isAdjustableBase && backendData.length) {
            return JSON.parse(backendData);
        }
    }
    return undefined;
};

/**
 *
 * MfrmAdjustableBaseDimensions component
 * @extends {React.PureComponent<IMfrmAdjustableBaseDimensionsProps<IMfrmAdjustableBaseDimensionsData>>}
 */
class MfrmAdjustableBaseDimensions extends React.PureComponent<
    IMfrmAdjustableBaseDimensionsProps<IMfrmAdjustableBaseDimensionsData>,
    IState
> {
    private validViews: IViewNames = {
        Flat: 'Flat',
        HeadUp: 'Head Up',
        FeetUp: 'Feet Up',
        ZeroGravity: 'Zero Gravity'
    };
    private initialSize: string = 'Queen';
    private adjustableBaseDimensions: IAdjustableBaseDimensions | undefined = parseBackendData(
        this.props.data.productSpecificationData.result,
        this.props.config.backendAttribute
    );
    private resizeThrottledEventHandler?: (event: Event) => void;

    constructor(props: IMfrmAdjustableBaseDimensionsProps<IMfrmAdjustableBaseDimensionsData>) {
        super(props);
        this.state = {
            selectedSize: this.initialSize,
            selectedView: this.initialView(),
            isMobile: false,
            isTablet: false
        };
    }

    public updateSize = (value: string) => {
        const defaultSelectedView =
            (this.adjustableBaseDimensions &&
                Object.keys(this.adjustableBaseDimensions[value]).find((key: string) => this.validViews[key])) ||
            '';
        this.setState({ selectedSize: value, selectedView: defaultSelectedView });
    };

    public updateView = (value: string) => {
        this.setState({ selectedView: value });
    };

    public componentDidMount(): void {
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', () => this._updateViewport());
        }
        this._updateViewport();
    }

    public componentWillUnmount(): void {
        Msdyn365.msdyn365Commerce.isBrowser && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
    }

    public render(): JSX.Element | null {
        // Do not render if there is no adjustable base data
        if (!this.adjustableBaseDimensions) {
            return null;
        }
        const { isMobile, isTablet } = this.state;

        const sizeButtonsData = this._getSizeButtonsData();
        const viewButtonsData = this._getViewButtonsData();

        const sizeDimensionsData = this._getSizeDimensionsData();
        const viewDimensionsData = this._getViewDimensionsData();

        const viewProps = {
            ...this.props,
            viewButtons: this._getViewButtons(viewButtonsData),
            selectedSizeDimensions: this._getSelectedSizeDimensions(sizeDimensionsData),
            selectedViewDimensions: this._getSelectedViewDimensions(viewDimensionsData),
            displayImage: this._getDisplayImage(),
            sizeSelection: isMobile || isTablet ? this._getDropdown(sizeButtonsData) : this._getSizeButtons(sizeButtonsData)
        };
        return this.props.renderView(viewProps);
    }

    private _updateViewport = () => {
        const { context } = this.props;
        const viewPort = _getViewPort(context);
        viewPort === 'xs' || viewPort === 'sm' ? this.setState({ isMobile: true }) : this.setState({ isMobile: false });
        viewPort === 'md' || viewPort === 'lg' ? this.setState({ isTablet: true }) : this.setState({ isTablet: false });
    };

    private dropdownCallback = (selectedSize: ISelectedSize): Promise<void> => {
        return new Promise((resolve, reject) => {
            resolve(this.updateSize(selectedSize.selectId));
        });
    };

    private initialView = () => {
        if (!this.adjustableBaseDimensions) {
            return '';
        }
        return Object.keys(this.adjustableBaseDimensions[this.initialSize]).find(key => this.validViews[key]) || '';
    };

    private _getViewDimensionsData = () => {
        const selectedViewDimensions = [];
        if (this.adjustableBaseDimensions) {
            const selectedSizeData = this.adjustableBaseDimensions[this.state.selectedSize];
            const selectedViewData = selectedSizeData[this.state.selectedView];

            if (this.state.selectedView === 'Flat') {
                // Flat view only displays one value for 'Height'
                if (selectedViewData?.MaxHeight) {
                    selectedViewDimensions.push({ name: 'Height', value: `${selectedViewData.MinHeight}"` });
                }
            } else if (this.state.selectedView === 'ZeroGravity') {
                // Zero Gravity displays different height text to reflect the image
                if (selectedViewData?.MaxHeight) {
                    selectedViewDimensions.push({ name: 'Head Height', value: `${selectedViewData.MaxHeight}"` });
                }

                if (selectedViewData?.MinHeight) {
                    selectedViewDimensions.push({ name: 'Feet Height', value: `${selectedViewData.MinHeight}"` });
                }
            } else {
                // All other views display both Min and Max values
                if (selectedViewData?.MaxHeight) {
                    selectedViewDimensions.push({ name: 'Max Height', value: `${selectedViewData.MaxHeight}"` });
                }

                if (selectedViewData?.MinHeight) {
                    selectedViewDimensions.push({ name: 'Min Height', value: `${selectedViewData.MinHeight}"` });
                }
            }

            // add Length and Width to all views
            if (selectedSizeData?.Length) {
                selectedViewDimensions.push({ name: 'Length', value: `${selectedSizeData.Length}"` });
            }
            if (selectedSizeData?.Width) {
                selectedViewDimensions.push({ name: 'Width', value: `${selectedSizeData.Width}"` });
            }
        }

        return selectedViewDimensions;
    };

    private _getSizeDimensionsData = () => {
        const selectedSizeDimensions: IDisplayValues[] = [];
        if (this.adjustableBaseDimensions) {
            const selectedSizeData = this.adjustableBaseDimensions[this.state.selectedSize];

            if (selectedSizeData.Length && selectedSizeData.Width) {
                selectedSizeDimensions.push({ name: 'Total Size', value: `${selectedSizeData.Width}" W x ${selectedSizeData.Length}" L` });
            }
            if (selectedSizeData.Weight) {
                selectedSizeDimensions.push({ name: 'Weight Capacity', value: `${selectedSizeData.Weight} lbs.` });
            }
        }

        return selectedSizeDimensions;
    };

    private _getSizeButtonsData = () => {
        let sizeButtons: ISizeButtonsData[] = [];
        if (this.adjustableBaseDimensions) {
            sizeButtons = Object.keys(this.adjustableBaseDimensions).map((keyName, _i) => {
                const sizeDimensions = `${this.adjustableBaseDimensions && this.adjustableBaseDimensions[keyName].Width}" x ${this
                    .adjustableBaseDimensions && this.adjustableBaseDimensions[keyName].Length}"`;
                return {
                    displayName: this.adjustableBaseDimensions && this.adjustableBaseDimensions[keyName].Name,
                    value: keyName || '',
                    sizeDimensions
                };
            });
        }
        return sizeButtons;
    };

    private _getViewButtonsData = () => {
        const viewButtons: IViewButtonsData[] = [];
        const selectedSize = this.state.selectedSize;
        if (this.adjustableBaseDimensions) {
            const selectedSizeData = this.adjustableBaseDimensions[selectedSize];
            const selectedSizeDataKeys = Object.keys(selectedSizeData);

            for (const property of selectedSizeDataKeys) {
                const viewProperty = this.validViews[property];
                if (viewProperty) {
                    viewButtons.push({ value: property, displayName: viewProperty });
                }
            }
        }

        return viewButtons;
    };

    private _getSizeButtons = (sizeButtonsData: ISizeButtonsData[]) => {
        return (
            <div className='mfrm-adj-base__sizeButtons-container'>
                {sizeButtonsData.map((button: ISizeButtonsData) => {
                    const selectedClassName = this.state.selectedSize === button.value ? 'selected-size' : '';

                    return (
                        // tslint:disable-next-line:react-this-binding-issue jsx-no-lambda
                        <Button
                            className={classNames('mfrm-adj-base__size-button', selectedClassName)}
                            key={button.value}
                            onClick={() => this.updateSize(button.value)}
                        >
                            {button.displayName} <br />
                            <span className='mfrm-adj-base__size-button-dimensions'> {button.sizeDimensions} </span>
                        </Button>
                    );
                })}
            </div>
        );
    };

    private _getDropdown = (sizeButtonsData: ISizeButtonsData[]) => {
        const dropdownList = sizeButtonsData.map((size: ISizeButtonsData) => {
            return {
                value: size.displayName || '',
                id: size.value,
                selectedValue: this.state.selectedSize
            };
        });

        return (
            <Node className='mfrm-adj-base__dropdown'>
                <Node className='mfrm-adj-base__dropdown-label'>Size</Node>
                <Dropdown
                    dropdownId='adjBaseDropdown'
                    dropdownName='adjBaseDropdown'
                    dropdownToggleName='adjBaseDropdownToggle'
                    dropdownList={dropdownList}
                    onChange={this.dropdownCallback}
                />
            </Node>
        );
    };

    private _getViewButtons = (viewButtonsData: IViewButtonsData[]) => {
        return (
            <ul className='mfrm-adj-base__view-wrapper'>
                {viewButtonsData.map((button: IViewButtonsData) => {
                    const selectedClassName = this.state.selectedView === button.value ? 'selected-view' : '';
                    return (
                        button && (
                            <li key={button.value} className='mfrm-adj-base__view-item'>
                                {/* tslint:disable-next-line:react-this-binding-issue jsx-no-lambda */}
                                <Button
                                    className={classNames('mfrm-adj-base__view-button', selectedClassName)}
                                    title={button.displayName}
                                    onClick={() => this.updateView(button.value)}
                                >
                                    {button.displayName}
                                </Button>
                            </li>
                        )
                    );
                })}
            </ul>
        );
    };

    private _getSelectedSizeDimensions = (selectedSizeDimensions: IDisplayValues[]) => {
        return (
            <div className='mfrm-adj-base__selected-size-dimensions'>
                {selectedSizeDimensions.map((data: IDisplayValues) => {
                    return (
                        <div key={data.name}>
                            {data.name} <span>{data.value}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    private _getSelectedViewDimensions = (selectedViewDimensions: IDisplayValues[]) => {
        return (
            <div className='row mfrm-adj-base__selected-view-dimensions'>
                {selectedViewDimensions.map((data: IDisplayValues) => {
                    return (
                        <div key={data.name} className='mfrm-adj-base__selected-view-data'>
                            {' '}
                            {data.name} <span>{data.value}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    private _getDisplayImage = () => {
        const {
            config: { headUpImage, flatImage, feetUpImage, zeroGravityImage }
        } = this.props;

        const images = {
            HeadUp: headUpImage,
            Flat: flatImage,
            ZeroGravity: zeroGravityImage,
            FeetUp: feetUpImage
        };
        const displayImageData: Msdyn365.IImageData | undefined = images[this.state.selectedView];
        return (
            <div className='mfrm-adj-base__image-container'>
                <Msdyn365.Image
                    {...displayImageData}
                    alt={displayImageData?.altText}
                    className='mfrm-adj-base__image'
                    src={displayImageData?.src || ''}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={displayImageData?.imageSettings}
                />
            </div>
        );
    };
}

export default MfrmAdjustableBaseDimensions;
